import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { Search, Calendar, ChevronRight } from 'lucide-react';
import type { Event, EventGroup, Result, SplitPoint } from '../types';
import Logo from './Logo';
import { Link } from 'react-router-dom';

interface EventWithResults extends Event {
  results: Result[];
}

export default function SharedGroupResults() {
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true'; 
  const isEmbed = searchParams.get('embed') === 'true';
  const [group, setGroup] = useState<EventGroup | null>(null);
  const [events, setEvents] = useState<EventWithResults[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadGroupAndEvents();
  }, [groupId]);

  useEffect(() => {
    if (searchTerm) {
      const delaySearch = setTimeout(() => {
        searchResults();
      }, 300);
      return () => clearTimeout(delaySearch);
    } else {
      loadGroupAndEvents();
    }
  }, [searchTerm]);

  const loadGroupAndEvents = async () => {
    if (!groupId) {
      setError('No group ID provided');
      setLoading(false);
      return;
    }

    try {
      // For preview mode, we'll still load group data but use sample results
      const resultsLimit = isPreview ? 3 : 5;
      
      // Try to load by ID or custom slug
      let groupQuery = supabase
        .from('event_groups')
        .select('*');
      
      // Check if groupId is a UUID or a custom slug
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(groupId);
      
      if (isUuid) {
        groupQuery = groupQuery.eq('id', groupId);
      } else {
        groupQuery = groupQuery.eq('custom_url_slug', groupId);
      }
      
      const { data: groupData, error: groupError } = await groupQuery.single();

      if (groupError) throw groupError;
      if (!groupData) throw new Error('Group not found');
      
      setGroup(groupData);
      
      if (!isPreview || isEmbed) {
        // Load real data for non-preview or embedded preview
        // Use the actual group ID from the database, not the URL parameter
        const { data: eventsData, error: eventsError } = await supabase
          .from('events')
          .select('*')
          .eq('group_id', groupData.id)
          .order('order_index', { ascending: true });

        if (eventsError) throw eventsError;

        const eventsWithResults = await Promise.all((eventsData || []).map(async (event) => {
          const { data: results } = await supabase
            .from('results')
            .select('*')
            .eq('event_id', event.id)
            .order('rank', { ascending: true, nullsFirst: false })
            .limit(resultsLimit);

          return {
            ...event,
            results: results || []
          };
        }));

        setEvents(eventsWithResults);
      } else {
        // For preview mode, use sample data
        setEvents([
          {
            id: '1',
            name: 'Sample Event 1',
            date: new Date().toISOString(),
            results: [
              { id: '1', event_id: '1', bib_number: '101', name: 'John Runner', rank: 1, chip_time: '1:23:45' },
              { id: '2', event_id: '1', bib_number: '102', name: 'Jane Athlete', rank: 2, chip_time: '1:24:30' }
            ]
          },
          {
            id: '2',
            name: 'Sample Event 2',
            date: new Date().toISOString(),
            results: [
              { id: '3', event_id: '2', bib_number: '201', name: 'Sam Sprinter', rank: 1, chip_time: '0:45:15' },
              { id: '4', event_id: '2', bib_number: '202', name: 'Alex Runner', rank: 2, chip_time: '0:46:20' }
            ]
          }
        ] as EventWithResults[]);
      }
    } catch (err: any) {
      console.error('Error loading group:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchResults = async () => {
    if (!searchTerm.trim() || !group?.id) return;
    
    setSearching(true);
    try {
      const { data: eventsData } = await supabase
        .from('events')
        .select('*')
        .eq('group_id', group.id)
        .order('order_index', { ascending: true });

      if (!eventsData) return;

      const eventsWithResults = await Promise.all(eventsData.map(async (event) => {
        const { data: results } = await supabase
          .from('results')
          .select('*')
          .eq('event_id', event.id) 
          .or(`name.ilike.%${searchTerm}%,bib_number.ilike.%${searchTerm}%`)
          .order('rank', { ascending: true })
          .limit(5);

        return {
          ...event,
          results: results || []
        };
      }));

      setEvents(eventsWithResults.filter(event => event.results.length > 0));
    } catch (err) {
      console.error('Error searching results:', err);
    } finally {
      setSearching(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="mb-6">
            <Logo className="h-8 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Group Not Found</h1>
          <p className="text-gray-600">
            {error || 'The requested group could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isEmbed ? '' : 'min-h-screen'} bg-gray-50 relative flex flex-col`}>
      {group && (
        <Helmet>
          <title>{group.name} Results – {formatDate(group.date)} | Pacer Results</title>
          <meta name="description" content={`Official results for ${group.name} on ${formatDate(group.date)}. View finish times and rankings across multiple events. Powered by Pacer Results.`} />
          <meta property="og:title" content={`${group.name} Results – ${formatDate(group.date)} | Pacer Results`} />
          <meta property="og:description" content={`Official results for ${group.name} on ${formatDate(group.date)}. View finish times and rankings across multiple events. Powered by Pacer Results.`} />
          <meta property="og:image" content={`${window.location.origin}/meta/default-share.jpg`} />
          <meta property="og:url" content={window.location.href} />
          <link rel="canonical" href={window.location.href} />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SportsEvent",
              "name": group.name,
              "startDate": group.date,
              "eventStatus": "https://schema.org/EventScheduled",
              "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
              "organizer": {
                "@type": "Organization",
                "name": "Pacer Results",
                "url": window.location.origin
              }
            })}
          </script>
        </Helmet>
      )}
      {group?.shared_results_background?.url && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${group.shared_results_background.url})`,
            opacity: group.shared_results_background.opacity
          }}
        />
      )}

      <div className="relative flex-1">
        <header className={`${isEmbed ? 'hidden' : ''} ${group?.shared_results_background ? 'bg-white/90 backdrop-blur-sm' : 'bg-white'} shadow`}>
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center h-16">
              {group?.shared_results_logo?.url ? (
                <img 
                  src={group.shared_results_logo.url} 
                  alt={group.name}
                  style={{ height: group.shared_results_logo.height }}
                  className="max-w-[200px] object-contain"
                />
              ) : (
                <Logo className="h-5 fill-black" />
              )}

              {group?.timing_company_logo && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Timed by</span>
                  <img 
                    src={group.timing_company_logo.url} 
                    alt={group.timing_company_logo.name}
                    className="h-8 max-w-[120px] object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </header>

        <main className={`container mx-auto px-${isEmbed ? '2' : '4'} py-${isEmbed ? '2' : '8'}`}>
          <div className="space-y-6">
            <div className={`${group?.shared_results_background ? 'bg-white/90 backdrop-blur-sm p-6 rounded-lg shadow' : ''}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{group?.name}</h1>
                  <p className="text-gray-600">{group && formatDate(group.date)}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className={`relative flex-1 ${isEmbed && !isPreview ? 'hidden' : ''}`}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name or bib number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 ${
                    group?.shared_results_background 
                      ? 'bg-white/90 backdrop-blur-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500'
                      : 'bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500'
                  } sm:text-sm`}
                />
              </div>
            </div>

            <div className={`grid gap-6 ${isEmbed ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
              {events.map(event => (
                <div
                  key={event.id}
                  className={`${
                    group?.shared_results_background 
                      ? 'bg-white/90 backdrop-blur-sm'
                      : 'bg-white'
                  } rounded-lg shadow overflow-hidden ${isEmbed ? 'max-h-[300px]' : ''}`}
                >
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">{event.name}</h3>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{formatDate(event.date)}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`divide-y divide-gray-200 ${isEmbed ? 'overflow-y-auto max-h-[200px]' : ''}`}>
                    {/* Headers */}
                    <div className="px-4 py-2 bg-gray-50 text-sm grid grid-cols-12 gap-4">
                      <div className="col-span-2 font-medium text-gray-500">POS</div>
                      <div className="col-span-6 font-medium text-gray-500">NAME</div>
                      <div className="col-span-4 text-right font-medium text-gray-500">
                        {event.show_gun_time_instead_of_chip_time ? 'GUN TIME' : 'CHIP TIME'}
                      </div>
                    </div>

                    {event.results.map(result => (
                      <div key={result.id} className="px-4 py-2 text-sm grid grid-cols-12 gap-4">
                        <div className="col-span-2 font-medium text-gray-900">#{result.rank || '-'}</div>
                        <div className="col-span-6 font-medium text-gray-900">{result.name}</div>
                        <div className="col-span-4 text-right text-gray-900">
                          {event.show_gun_time_instead_of_chip_time 
                            ? (result.gun_time || result.chip_time || '-') 
                            : (result.chip_time || '-')}
                        </div>
                      </div>
                    ))}
                    {event.results.length === 0 && (
                      <div className="px-4 py-4 text-center text-sm text-gray-500">
                        No results found
                      </div>
                    )}
                  </div>

                  <div className={`p-4 bg-gray-50 border-t border-gray-200 ${isEmbed && isPreview ? 'hidden' : ''}`}>
                    <Link
                      to={`/results/${event.id}`}
                      state={{ fromGroup: true }}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-black text-white rounded-md hover:bg-black text-sm font-medium"
                    >
                      View Full Results
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {events.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? 'No matching results found' : 'No events found in this group'}
              </div>
            )}
          </div>
        </main>
      </div>

      <footer className={`${isEmbed ? 'hidden' : ''} ${
        group?.shared_results_background 
          ? 'bg-white/80 backdrop-blur-sm border-t border-gray-200/50'
          : 'bg-white border-t border-gray-200'
      } py-3 mt-auto`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-gray-500">
            <div>
              © {new Date().getFullYear()} Pacer Results
            </div>
            <div className="flex gap-4 mt-2 sm:mt-0">
              <Link to="/terms" className="hover:text-gray-700">Terms</Link>
              <Link to="/privacy" className="hover:text-gray-700">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}